import { BaseComponent, BuildingStructure, PhotoModel } from '../types/protocols';
import { BaseType } from '../types/Enums';

/**
 * The takplan is a view, not a level: the set of roof components, drawn on
 * the satellite image (`mapLocations`) and on the building's roof drawing at
 * `structure/roofPlan` (`blueprintLocations`). Pure helpers over plain data:
 * no Firebase and no `implementations` import, so the navigator and the
 * tests can load this without the Firebase SDK.
 * See docs/plans/2026-09-24-refactor-takplan-roof-components-plan.md.
 */

export const ROOF_PLAN_LABEL = 'Takplan';

export const roofPlanPath = (buildingKey: string): string => `buildings/${buildingKey}/structure/roofPlan`;
export const roofPlanBlueprintsPath = (buildingKey: string): string => `${roofPlanPath(buildingKey)}/blueprints`;
export const roofPlanCurrentBlueprintKeyPath = (buildingKey: string): string => `${roofPlanPath(buildingKey)}/currentBlueprintKey`;

/** The current roof drawing, or null when none is uploaded (or the pointer dangles). */
export const currentRoofPlanBlueprint = (
    structure: BuildingStructure | undefined
): { key: string; image: PhotoModel } | null => {
    const roofPlan = structure?.roofPlan;
    const key = roofPlan?.currentBlueprintKey;
    const image = key ? roofPlan?.blueprints?.[key] : undefined;
    return key && image ? { key, image } : null;
};

const isLive = (component: BaseComponent | undefined | null): component is BaseComponent =>
    !!component && !component.archivedAt;

/** Keys of the building's live `roof` components (structural roofs are their own base type and never count). */
export const liveRoofKeys = (structure: BuildingStructure | undefined): Set<string> =>
    new Set(
        Object.entries(structure?.components || {})
            .filter(([, component]) => isLive(component) && component.type === BaseType.Roof)
            .map(([key]) => key)
    );

/**
 * D6: the roof a component sits on — `parentSurface` or `parentComponentKey`
 * pointing at a roof, or for a window (takfönster) either of its two parents.
 * Null for roofs themselves and for everything not mounted on a roof.
 */
export const roofPlanParentRoofKey = (component: BaseComponent, roofKeys: Set<string>): string | null => {
    if (component.type === BaseType.Roof) return null;
    const mounted = component.parentSurface?.value;
    if (mounted && roofKeys.has(mounted)) return mounted;
    if (component.parentComponentKey && roofKeys.has(component.parentComponentKey)) return component.parentComponentKey;
    if (component.type === BaseType.Window) {
        const first = component.parentSurface1?.value;
        if (first && roofKeys.has(first)) return first;
        const second = component.parentSurface2?.value;
        if (second && roofKeys.has(second)) return second;
    }
    return null;
};

/** D6: a component is part of the takplan when it is a roof or sits on one (see `roofPlanParentRoofKey`). */
export const isRoofPlanComponent = (component: BaseComponent, roofKeys: Set<string>): boolean =>
    component.type === BaseType.Roof || roofPlanParentRoofKey(component, roofKeys) !== null;

/** Live D6 components of the building (each with its key), in no particular order. */
export const roofPlanComponents = (structure: BuildingStructure | undefined): BaseComponent[] => {
    const roofKeys = liveRoofKeys(structure);
    return Object.entries(structure?.components || {})
        .filter(([, component]) => isLive(component) && isRoofPlanComponent(component, roofKeys))
        .map(([key, component]) => (component.key ? component : { ...component, key }));
};

/** D18: the takplan pill/page exists only when there is something to show. */
export const hasRoofPlanView = (structure: BuildingStructure | undefined): boolean =>
    currentRoofPlanBlueprint(structure) !== null || roofPlanComponents(structure).length > 0;

/** Where a utility is mounted when it is not in a room (D3/D4). */
export type MountingSurface = 'roof' | 'facade';

export const MOUNTING_SURFACE_LABELS: Record<MountingSurface, string> = {
    roof: 'Tak',
    facade: 'Fasad',
};

/**
 * A utility with `parentSurface` pointing at a roof or facade and no
 * `parentRoomKeys` is mounted, not room-bound. Null for every other
 * component and for a utility whose parent is missing or of another type.
 */
export const utilityMountingSurface = (
    component: Pick<BaseComponent, 'type' | 'parentSurface' | 'parentRoomKeys'>,
    components: { [key: string]: BaseComponent } | undefined
): MountingSurface | null => {
    if (component.type !== BaseType.Utility) return null;
    if ((component.parentRoomKeys?.value?.length ?? 0) > 0) return null;
    const parentKey = component.parentSurface?.value;
    const parent = parentKey ? components?.[parentKey] : undefined;
    if (!parent) return null;
    if (parent.type === BaseType.Roof) return 'roof';
    if (parent.type === BaseType.Facade) return 'facade';
    return null;
};
